/**
 * Board Creation Wizard Reducer
 * 
 * Centralizes all wizard state management with explicit actions.
 * This reducer will eventually be migrated to a Zustand store.
 */

import { WizardState, WizardAction, WizardStep, WizardPath } from '@/types/state';

export const initialWizardState: WizardState = {
  currentStep: 'path-selection',
  selectedPath: null,
  threadId: null,
  problem: '',
  context: '',
  brainDump: '',
  suggestedPersonas: [],
  showResumePrompt: false,
  lastSaved: null,
  boardDetails: null,
  fullPersonas: [],
  isGenerating: false,
  generationStatus: null,
  remixBoard: null,
};

/**
 * Helper: Determine next step based on selected path
 */
function getStepForPath(path: WizardPath): WizardStep {
  switch (path) {
    case 'prebuilt':
      return 'prebuilt-selection';
    case 'custom':
      return 'custom-form';
    case 'braindump':
      return 'braindump-form';
  }
}

/**
 * Helper: Get previous step based on current step and path
 */
function getPreviousStep(currentStep: WizardStep, selectedPath: WizardPath | null): WizardStep {
  if (currentStep === 'persona-suggestions') {
    return selectedPath === 'custom' ? 'custom-form' : 'braindump-form';
  }
  if (currentStep === 'review-board-details') {
    return selectedPath === 'custom' ? 'custom-form' : 'braindump-form';
  }
  if (currentStep === 'review-personas') {
    return 'review-board-details';
  }
  if (currentStep === 'final-review') {
    return 'review-personas';
  }
  return 'path-selection';
}

/**
 * Wizard Reducer
 * 
 * Handles all state transitions for the board creation wizard.
 * Each action is explicit and can be logged for debugging.
 */
export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  // Log action in development for debugging
  if (import.meta.env.DEV) {
    console.log('[WizardReducer]', action.type, action);
  }

  switch (action.type) {
    case 'INITIALIZE':
      // Initialize wizard from saved state (e.g., localStorage)
      return {
        ...state,
        ...action.payload,
        showResumePrompt: true,
      };

    case 'SELECT_PATH':
      // User selects a wizard path (prebuilt, custom, or braindump)
      return {
        ...state,
        selectedPath: action.payload,
        currentStep: getStepForPath(action.payload),
        showResumePrompt: false,
        remixBoard: null, // Clear remix board when selecting a new path
      };

    case 'UPDATE_PROBLEM':
      // User updates problem description (custom path)
      return {
        ...state,
        problem: action.payload,
      };

    case 'UPDATE_CONTEXT':
      // User updates additional context (custom path)
      return {
        ...state,
        context: action.payload,
      };

    case 'UPDATE_BRAIN_DUMP':
      // User updates brain dump content (braindump path)
      return {
        ...state,
        brainDump: action.payload,
      };

    case 'SET_SUGGESTED_PERSONAS':
      // AI generates persona suggestions
      return {
        ...state,
        suggestedPersonas: action.payload,
        currentStep: 'persona-suggestions',
      };

    case 'SET_GENERATED_BOARD':
      // AI generates complete board with details and personas
      return {
        ...state,
        boardDetails: action.payload.boardDetails,
        fullPersonas: action.payload.personas,
        currentStep: 'final-review',
      };

    case 'SET_PARTIAL_BOARD':
      // LangGraph interrupted for human feedback
      const nextStep = action.payload.personas ? 'review-personas' : 'review-board-details';
      return {
        ...state,
        threadId: action.payload.threadId,
        boardDetails: action.payload.boardDetails || state.boardDetails,
        fullPersonas: action.payload.personas || state.fullPersonas,
        currentStep: nextStep,
      };

    case 'SET_THREAD_ID':
      return {
        ...state,
        threadId: action.payload,
      };

    case 'NAVIGATE_TO_STEP':
      // Explicit navigation to a specific step
      return {
        ...state,
        currentStep: action.payload,
      };

    case 'GO_BACK':
      // User clicks back button
      if (state.currentStep === 'path-selection') {
        // Already at first step, no-op (component will handle navigation)
        return state;
      }
      return {
        ...state,
        currentStep: getPreviousStep(state.currentStep, state.selectedPath),
      };

    case 'MARK_SAVED':
      // Progress saved to localStorage
      return {
        ...state,
        lastSaved: action.payload,
      };

    case 'HIDE_RESUME_PROMPT':
      // User dismisses resume prompt
      return {
        ...state,
        showResumePrompt: false,
      };

    case 'RESET_WIZARD':
      // User starts fresh or error recovery
      return {
        ...initialWizardState,
      };

    case 'COMPLETE_WIZARD':
      // Wizard completed successfully
      return {
        ...initialWizardState,
      };

    case 'SET_SESSION_ID':
      // Set tracking session ID
      return {
        ...state,
        sessionId: action.payload,
      };

    case 'START_GENERATION':
      // Start generation process
      return {
        ...state,
        isGenerating: true,
        generationStatus: { step: 'starting', message: 'Initiating generation...' },
      };

    case 'UPDATE_GENERATION_STATUS':
      // Update generation progress and partial data
      return {
        ...state,
        generationStatus: action.payload,
        // Merge partial data if available
        boardDetails: (action.payload as any).boardDetails || state.boardDetails,
        fullPersonas: (action.payload as any).personas || state.fullPersonas,
      };

    case 'END_GENERATION':
      // End generation process
      return {
        ...state,
        isGenerating: false,
        generationStatus: null,
      };

    case 'START_REMIX':
      return {
        ...state,
        remixBoard: action.payload,
        selectedPath: 'custom',
        currentStep: 'custom-form',
        // Clear previous form data to start fresh with the remix
        problem: '',
        context: '',
        brainDump: '',
      };

    default:
      // TypeScript should ensure this is never reached
      const _exhaustive: never = action;
      console.warn('[WizardReducer] Unknown action:', _exhaustive);
      return state;
  }
}

/**
 * Action Creators (Optional - for better ergonomics)
 * 
 * These are optional but make dispatching actions more type-safe:
 * dispatch(selectPath('custom')) vs dispatch({ type: 'SELECT_PATH', payload: 'custom' })
 */

export const wizardActions = {
  initialize: (payload: Partial<WizardState>): WizardAction => ({
    type: 'INITIALIZE',
    payload,
  }),

  selectPath: (payload: WizardPath): WizardAction => ({
    type: 'SELECT_PATH',
    payload,
  }),

  updateProblem: (payload: string): WizardAction => ({
    type: 'UPDATE_PROBLEM',
    payload,
  }),

  updateContext: (payload: string): WizardAction => ({
    type: 'UPDATE_CONTEXT',
    payload,
  }),

  updateBrainDump: (payload: string): WizardAction => ({
    type: 'UPDATE_BRAIN_DUMP',
    payload,
  }),

  setSuggestedPersonas: (payload: typeof initialWizardState.suggestedPersonas): WizardAction => ({
    type: 'SET_SUGGESTED_PERSONAS',
    payload,
  }),

  setGeneratedBoard: (payload: { boardDetails: any; personas: any[] }): WizardAction => ({
    type: 'SET_GENERATED_BOARD',
    payload,
  }),

  setPartialBoard: (payload: { boardDetails?: any; personas?: any[]; threadId: string }): WizardAction => ({
    type: 'SET_PARTIAL_BOARD',
    payload,
  }),

  setThreadId: (payload: string): WizardAction => ({
    type: 'SET_THREAD_ID',
    payload,
  }),

  navigateToStep: (payload: WizardStep): WizardAction => ({
    type: 'NAVIGATE_TO_STEP',
    payload,
  }),

  goBack: (): WizardAction => ({
    type: 'GO_BACK',
  }),

  markSaved: (payload: number): WizardAction => ({
    type: 'MARK_SAVED',
    payload,
  }),

  hideResumePrompt: (): WizardAction => ({
    type: 'HIDE_RESUME_PROMPT',
  }),

  resetWizard: (): WizardAction => ({
    type: 'RESET_WIZARD',
  }),

  completeWizard: (): WizardAction => ({
    type: 'COMPLETE_WIZARD',
  }),

  setSessionId: (payload: string): WizardAction => ({
    type: 'SET_SESSION_ID',
    payload,
  }),

  startGeneration: (): WizardAction => ({ type: 'START_GENERATION' }),
  
  updateGenerationStatus: (payload: { step: string; message: string }): WizardAction => ({
    type: 'UPDATE_GENERATION_STATUS',
    payload,
  }),
  
  endGeneration: (): WizardAction => ({ type: 'END_GENERATION' }),

  startRemix: (payload: NonNullable<WizardState['remixBoard']>): WizardAction => ({
    type: 'START_REMIX',
    payload,
  }),
};
